import { ChangeEvent, DropdownItem, Textbox } from "@mcleod/components";
import { AutogenLayoutDistanceRateConfig } from "./autogen/AutogenLayoutDistanceRateConfig";
import { Api, LogManager, ModelRow } from "@mcleod/core";

const MILEMAKER_GUIDE19_VEHICLE_TYPE = [new DropdownItem("T", "Truck")];

const MILEMAKER_GUIDE19_TRAILER_LENGTH = [new DropdownItem("A", "Single 48ft"),
                                          new DropdownItem("B", "Single 53ft")];

const MILEMAKER_GUIDE20_VEHICLE_TYPE = [new DropdownItem("T", "Truck"),
                                        new DropdownItem("C", "Car / Van / Light Truck")];

const MILEMAKER_GUIDE20_TRAILER_LENGTH = [new DropdownItem("I", "Straight"),
                                          new DropdownItem("A", "Single 48ft"),
                                          new DropdownItem("B", "Single 53ft"),
                                          new DropdownItem("D", "Double"),
                                          new DropdownItem("T", "Triple")];

const PCMILER = { WEB: "T", ON_PREM: "A"};
const MILEMAKER = { WEB: "E", INTELLIROUTE: "R"};

const log = LogManager.getLogger("dispatch.src.DistanceRateConfig");

export class DistanceRateConfig extends AutogenLayoutDistanceRateConfig {

    override onLoad(): void {
        this.getDistanceVendors().then(() => {
            // After vendors are loaded, get the selected value and populate dropdowns
            const selectedItem = this.textboxServerToUse.selectedItem;
            if (selectedItem) {
                const dropdownValue = selectedItem.value;
                this.populateDistanceDropdownItems(dropdownValue);
            }
        });
    }

    /** This is an event handler for the onChange event of server to use textbox.  */
    textboxServerToUseOnChange(event: ChangeEvent) {
        const selectedItem = (event.target as Textbox).selectedItem;
        if (selectedItem) {
            const dropdownValue = selectedItem.value;

            // Show version textbox is PC*Miler Web or MileMaker
            const displayVersions = PCMILER.WEB === dropdownValue || MILEMAKER.WEB === dropdownValue;
            this.setDistanceVersionsVisibility(displayVersions);

            this.populateDistanceDropdownItems(dropdownValue);
        }
    }

    /** This is an event handler for the dropdownSelection event of server to use textbox.  */
    textboxServerToUseAfterDropdownSelection() {
        // Clear the dropdown value so the user must select a new value per the previous value may not be applicable for the vendor.
        this.textboxLookupType.clear();
        this.textboxLookupBy.clear();
    }

    async populateDistanceDropdownItems(dropdownValue: string) {
        // Populate Distance dropdown items
        await this.getDistanceSourceVersions(dropdownValue);
        await this.getLookupTypes(dropdownValue)
        await this.getLookupMethods(dropdownValue);

        this.displayDistanceOptions(dropdownValue);
    }

    /** This is an event handler for the onChange event of version textbox.  */
    textboxVersionOnChange(event: ChangeEvent) {
        const selectedItem = (event.target as Textbox).selectedItem;
        if (selectedItem) {
            const dropdownValue = selectedItem.value;

            if (this.textboxServerToUse.text === "MileMaker") {
                this.displayMileMakerGuideOptions(dropdownValue);
            }
        }
    }

    /** This is an event handler for the onChange event of lookup type textbox.  */
    textboxLookupTypeOnChange(event: ChangeEvent) {
        const selectedItem = (event.target as Textbox).selectedItem;
        if (selectedItem) {
            const dropdownValue = selectedItem.displayValue;

            const typeNotLatLong = dropdownValue !== "Lat/Long";

            this.textboxLookupBy.visible = typeNotLatLong;
            this.textboxLookupBy.required = typeNotLatLong;
        }
    }

    public async setPanel1ServerToUse(shortProfileName: string) {
        const response = await Api.search("lme/dispatch/distance-profile", { profile_id: shortProfileName });
        const shortProfileRow: ModelRow[] = response.data.filter((row: any) => row.id === shortProfileName) as ModelRow[];
        const values = { ...shortProfileRow[0] };
        this.setUIFromProfileValues(values);
        if (this.sourceDistanceProfile.activeRow) {
            this.sourceDistanceProfile.activeRow.setValues(values);
        }
    }

    public async setPanel2ServerToUse(pracProfileName: string) {
        const response = await Api.search("lme/dispatch/distance-profile", { profile_id: pracProfileName });
        const pracProfileRow: ModelRow[] = response.data.filter((row: any) => row.id === pracProfileName) as ModelRow[];
        const values = { ...pracProfileRow[0] };
        this.setUIFromProfileValues(values);
        if (this.sourceDistanceProfile.activeRow) {
            this.sourceDistanceProfile.activeRow.setValues(values);
        }
    }

    async getDistanceSourceVersions(selectedItem: string) {
        const response = await Api.search("lme/dispatch/distance/distance-source-versions", { source: selectedItem});

        const versionIds = response.data[0].version_id;
        this.textboxVersion.items = versionIds;
        if (this.sourceDistanceProfile.activeRow) {
            this.textboxVersion.setSelectedItemFromDisplayValue(this.sourceDistanceProfile.activeRow?.get("version"));
        }
    }

    private async getDistanceVendors() {
        const response = await Api.search("lme/dispatch/distance/distance-vendors");

        const vendors = response.data;
        if (vendors.length === 0) {
            log.error("No distance vendors found. Please check your configuration.");
            return;
        }
        // Set the items for the textboxServerToUse dropdown
        const dropdownItems = [];
        for (const vendor of vendors) {
            dropdownItems.push(new DropdownItem(vendor.id, vendor.descr));
        }
        this.textboxServerToUse.items = dropdownItems;
        if (this.sourceDistanceProfile.activeRow) {
            this.textboxServerToUse.setSelectedItemFromDisplayValue(this.sourceDistanceProfile.activeRow?.get("source"));
        }
    }

    async getLookupTypes(selectedItem: string) {
        const response = await Api.search("lme/dispatch/distance/distance-lookup-types", { source: selectedItem});

        const lookupTypes = response.data;
        if (lookupTypes.length === 0) {
            log.error("No lookup types found for the selected distance vendor. Please check your configuration.");
            return;
        }
        // Set the items for the textboxLookupType dropdown
        const dropdownItems = [];
        for (const type of lookupTypes) {
            dropdownItems.push(new DropdownItem(type.type_id, type.descr));
        }
        this.textboxLookupType.items = dropdownItems;
    }

    async getLookupMethods(selectedItem: string) {
        const response = await Api.search("lme/dispatch/distance/distance-lookup-methods", { source: selectedItem});

        const lookupMethods = response.data;
        if (lookupMethods.length === 0) {
            log.error("No lookup methods found for the selected distance vendor. Please check your configuration.");
            return;
        }
        // Set the items for the textboxLookupBy dropdown
        const dropdownItems = [];
        for (const method of lookupMethods) {
            dropdownItems.push(new DropdownItem(method.method_id, method.descr));
        }
        this.textboxLookupBy.items = dropdownItems;
    }

    private displayDistanceOptions(dropdownValue: string) {
        this.hideDistanceOptions();
        switch (dropdownValue) {
            case PCMILER.ON_PREM:
            case PCMILER.WEB:
                this.panelPcMilerDistOptions.visible = true;
                break;
            case MILEMAKER.WEB:
                this.panelMileMakerDistanceOptions.visible = true;
                break;
            case MILEMAKER.INTELLIROUTE:
                this.panelIntellirouteDistanceOptions.visible = true;
                break;
            default:
                console.log("Distance Options have not been configured for " + dropdownValue);
        }
    }

    displayMileMakerGuideOptions(dropdownValue: string) {
        const isGuide19 = dropdownValue.toLowerCase() === "guide19";

        this.panelMileMakerGuide20Checkboxes.visible = !isGuide19;
        this.panelMileMakerGuide20Options.visible = !isGuide19;
        this.textboxMileMakerVehicleType.enabled = !isGuide19;
        this.textboxMileMakerVehicleType.items = isGuide19 ? MILEMAKER_GUIDE19_VEHICLE_TYPE : MILEMAKER_GUIDE20_VEHICLE_TYPE;
        this.textboxMileMakerTrailerLength.items = isGuide19 ? MILEMAKER_GUIDE19_TRAILER_LENGTH : MILEMAKER_GUIDE20_TRAILER_LENGTH;
        this.textboxMileMakerVehicleType.setSelectedItemFromDisplayValue("Truck");
        this.textboxMileMakerTrailerLength.setSelectedItemFromDisplayValue("Single 53ft");
    }

    private hideDistanceOptions() {
        this.panelPcMilerDistOptions.visible = false;
        this.panelIntellirouteDistanceOptions.visible = false;
        this.panelMileMakerDistanceOptions.visible = false;
    }

    private setDistanceVersionsVisibility(isVisible: boolean) {
        this.textboxVersion.visible = isVisible;
        this.textboxVersion.required = isVisible;
        if (!isVisible) {
            this.textboxVersion.dataSource.activeRow.set("version", null);
        }
    }

    private setUIFromProfileValues(values: any) {
        // Hide all option panels first
        this.hideDistanceOptions();

        // Set the values for the dropdowns and checkboxes so that they reflect the source, type, method, and hazmat values when
        // we call the route and in state distance APIs.
        switch (values.source) {
            case PCMILER.ON_PREM: // 'A'
            case PCMILER.WEB:     // 'T'
                this.panelPcMilerDistOptions.visible = true;
                this.textboxServerToUse.setSelectedItemFromDisplayValue(values.source);
                if (PCMILER.WEB === values.source) {
                    this.textboxVersion.setSelectedItemFromDisplayValue(values.version);
                }
                this.textboxLookupType.setSelectedItemFromDisplayValue(values.lookup_type);
                this.textboxLookupBy.setSelectedItemFromDisplayValue(values.method);
                this.checkboxHazmatRouting.checked = values.hazmat_routing === "Y";
                break;
            case MILEMAKER.WEB:   // 'E'
                this.panelMileMakerDistanceOptions.visible = true;
                this.textboxServerToUse.setSelectedItemFromDisplayValue(values.source);
                this.textboxVersion.setSelectedItemFromDisplayValue(values.version);
                this.displayMileMakerGuideOptions(values.version);
                this.textboxLookupType.setSelectedItemFromDisplayValue(values.lookup_type);
                this.textboxLookupBy.setSelectedItemFromDisplayValue(values.method);
                this.textboxMileMakerHazmatTypes.setSelectedItemFromValue(values.mm_hazmat_types);
                break;
            case MILEMAKER.INTELLIROUTE: // 'R'
                this.panelIntellirouteDistanceOptions.visible = true;
                this.textboxServerToUse.setSelectedItemFromDisplayValue(values.source);
                this.textboxLookupType.setSelectedItemFromDisplayValue(values.lookup_type);
                this.textboxLookupBy.setSelectedItemFromDisplayValue(values.method);
                this.checkboxHazmatRouting.checked = values.hazmat_routing === "Y";
                break;
            default:
                log.error(`Distance source '${values.source}' is not recognized. No options will be displayed.`);
                break;
        }

        // If lookup_type is Lat/Long, clear method and hide the method textbox
        if (values.lookup_type?.toLowerCase() === "lat/long" || values.lookup_type === "L") {
            this.textboxLookupBy.setSelectedItemFromDisplayValue(null);
            this.textboxLookupBy.visible = false;
        } else {
            this.textboxLookupBy.visible = true;
        }
    }
}
